import { QUESTION_TRANSLATABLE_FIELDS } from '@course-authoring/shared-types';
import type { JSONContent } from '@tiptap/core';
import type {
  CategorizationBlock,
  CategorizationCategory,
  CategorizationItem,
  CourseBlock,
  FreeformTextBlock,
  LikertHealthyResponse,
  LikertScaleBlock,
  LikertScaleOptionCount,
  LikertScaleType,
  QuestionBlockType,
  QuestionDefinitionDto,
  QuestionDraft,
  QuestionTranslationField,
  QuestionTranslationFields,
  SingleChoiceAnswerBlock,
  SpectrumColor,
  WorkplaceColorSpectrumBlock,
} from '@course-authoring/shared-types';
import {
  getLikertScaleOptionLabels,
  LIKERT_OPTION_COUNTS,
  LIKERT_SCALE_OPTIONS,
} from '@course-authoring/shared-types';
import { EMPTY_RICH_TEXT_DOC } from '../interactionEditorUtils';

/**
 * The draft schema's rich-text output type requires `type: string` while
 * TipTap's JSONContent leaves it optional. Every real document carries
 * `type: 'doc'`, so this narrowing cast is safe — `questionDraftSchema`
 * re-validates the shape before anything is submitted.
 */
type DraftRichText = JSONContent & { type: string };

const toDraftRichText = (content: JSONContent): DraftRichText =>
  content as DraftRichText;

/**
 * `purpose` is bank-level-only metadata (NA-389/NA-444) with no persisted
 * lesson-block counterpart — `FreeformTextBlock` in blocks.ts correctly has
 * no `purpose` field. This editing-scratch union is the one place that needs
 * to carry it transiently: `QuestionForm`'s Freeform editor keeps `purpose`
 * on the same in-memory draft object as every other field (via the shared
 * `onFieldChange` merge) so `blockToQuestionDraft` can read it back out. The
 * block-mode (lesson editor) path never uses this scratch shape, never shows
 * a Purpose control, and never persists the key.
 */
export type FreeformTextQuestionDraftBlock = FreeformTextBlock & {
  purpose?: 'Sentiment' | 'Reflection';
};

export type QuestionBlock =
  | LikertScaleBlock
  | SingleChoiceAnswerBlock
  | WorkplaceColorSpectrumBlock
  | CategorizationBlock
  | FreeformTextQuestionDraftBlock;

export const isQuestionBlock = (block: CourseBlock): block is QuestionBlock =>
  block.type === 'likert-scale'
  || block.type === 'single-choice-answer'
  || block.type === 'workplace-color-spectrum'
  || block.type === 'categorization'
  || block.type === 'freeform-text';

const SPECTRUM_COLORS: readonly SpectrumColor[] = ['green', 'yellow', 'orange', 'red'];

const spectrumLabel = (color: SpectrumColor): string =>
  color.charAt(0).toUpperCase() + color.slice(1);

const spectrumColorFromLabel = (label: string): SpectrumColor => {
  const normalized = label.trim().toLowerCase();
  return (SPECTRUM_COLORS as readonly string[]).includes(normalized)
    ? normalized as SpectrumColor
    : 'green';
};

/**
 * A block's in-memory child-row id is either a persisted numeric-string id
 * (seeded via `dtoToBlockFields`, e.g. `"101"`) or a freshly-added row's
 * `crypto.randomUUID()`. Only the former should round-trip to the server as
 * the draft's stable `id` field — the same distinction the NA-198
 * option-removal lockout already relies on (see `lockedOptionIds` etc. in
 * `QuestionCreatePanel.tsx`).
 */
const persistedIdOrUndefined = (id: string): number | undefined =>
  /^\d+$/.test(id) ? Number(id) : undefined;

/**
 * Converts a (draft) question block's authored fields into the wire
 * QuestionDraft the create endpoint consumes. Likert/WCS options are
 * generated here — the bank stores materialized option rows.
 */
export function blockToQuestionDraft(
  block: QuestionBlock,
  isPublic: boolean,
  externalAccountId: number | null = null,
): QuestionDraft {
  const questionText = toDraftRichText(block.description ?? EMPTY_RICH_TEXT_DOC);

  if (block.type === 'likert-scale') {
    const scale = block.scale ?? 'agreement';
    const optionCount = block.optionCount ?? 7;
    const healthy = block.healthyResponse ?? 'none';
    const labels = getLikertScaleOptionLabels(scale, optionCount);
    return {
      blockType: 'likert-scale',
      questionText,
      // NA-384: the bank's own Title, authored in this form — distinct from a
      // lesson block's title, which this synthetic draft block never carries.
      title: block.title,
      isPublic,
      externalAccountId,
      options: labels.map((label, index) => ({
        label,
        isTarget: (healthy === 'left' && index === 0)
          || (healthy === 'right' && index === labels.length - 1),
      })),
    };
  }

  if (block.type === 'single-choice-answer') {
    const gated = block.gated;
    const targetId = gated ? block.correctOptionId : block.healthyOptionId;
    return {
      blockType: 'single-choice-answer',
      gated,
      questionText,
      title: block.title,
      isPublic,
      externalAccountId,
      options: block.options.map((option) => ({
        id: persistedIdOrUndefined(option.id),
        label: option.label,
        isTarget: option.id === targetId && targetId !== undefined,
      })),
      correctAnswerExplanation: gated && block.correctExplanation
        ? toDraftRichText(block.correctExplanation)
        : undefined,
    };
  }

  if (block.type === 'workplace-color-spectrum') {
    const correctAnswer = block.correctAnswer ?? 'green';
    return {
      blockType: 'workplace-color-spectrum',
      questionText,
      title: block.title,
      isPublic,
      externalAccountId,
      options: SPECTRUM_COLORS.map((color) => ({
        label: spectrumLabel(color),
        isTarget: color === correctAnswer,
      })),
      correctAnswerExplanation: block.correctAnswerExplanation
        ? toDraftRichText(block.correctAnswerExplanation)
        : undefined,
    };
  }

  if (block.type === 'freeform-text') {
    return {
      blockType: 'freeform-text',
      questionText,
      title: block.title,
      isPublic,
      externalAccountId,
      purpose: block.purpose ?? 'Sentiment',
    };
  }

  // imageLibraryItemId here is whatever the picker actually handed out — an
  // encoded sqids slug, not a raw number (a Number() coercion on that slug
  // produces NaN and fails the server's validation; the server resolves the
  // real shape, the same as every other block type's image field already
  // works).
  const categories = block.categories.map((category) => ({
    id: persistedIdOrUndefined(category.id),
    tempId: `tmp_${category.id}`,
    label: category.label,
    emoji: category.icon?.type === 'emoji' ? category.icon.value : null,
    imageLibraryItemId: category.icon?.type === 'image' && category.icon.imageLibraryItemId
      ? category.icon.imageLibraryItemId
      : null,
  }));
  return {
    blockType: 'categorization',
    questionText,
    title: block.title,
    isPublic,
    externalAccountId,
    categories,
    items: block.items.map((item) => ({
      id: persistedIdOrUndefined(item.id),
      label: item.label,
      correctCategoryTempId: `tmp_${item.correctCategoryId}`,
      emoji: item.icon?.type === 'emoji' ? item.icon.value : null,
      imageLibraryItemId: item.icon?.type === 'image' && item.icon.imageLibraryItemId
        ? item.icon.imageLibraryItemId
        : null,
    })),
  };
}

/** Reverse-derives the Likert scale from stored option labels (labels are the truth). */
export function deriveLikertSettings(dto: QuestionDefinitionDto): {
  scale: LikertScaleType;
  optionCount: LikertScaleOptionCount;
  healthyResponse: LikertHealthyResponse;
} {
  const labels = dto.answerOptions.map((option) => option.label);
  const optionCount: LikertScaleOptionCount = labels.length === 5 ? 5 : 7;
  let scale: LikertScaleType = 'agreement';
  outer: for (const { value } of LIKERT_SCALE_OPTIONS) {
    for (const count of LIKERT_OPTION_COUNTS) {
      const candidate = getLikertScaleOptionLabels(value, count);
      if (candidate.length === labels.length && candidate.every((label, i) => label === labels[i])) {
        scale = value;
        break outer;
      }
    }
  }
  const targetIndex = dto.answerOptions.findIndex((option) => option.isTarget);
  const healthyResponse: LikertHealthyResponse = targetIndex === -1
    ? 'none'
    : targetIndex === 0 ? 'left' : 'right';
  return { scale, optionCount, healthyResponse };
}

/**
 * Materializes a question DTO into the block's legacy field names for
 * display/preview. These fields are display-only for referenced questions:
 * serialization strips them on save (the reference is the persisted truth,
 * server reads re-resolve them). Categorization feedback is authored directly
 * on the block (no copy-down) and persists as block-local editable fields.
 */
/**
 * Prepends "[COPY] " to a rich-text doc's leading text run, for seeding a
 * Duplicate draft (NA-196). Inserts into the first paragraph's first text
 * node if one exists, or adds one if the leading paragraph is empty.
 */
export function prependCopyPrefix(doc: JSONContent): JSONContent {
  const cloned = JSON.parse(JSON.stringify(doc)) as JSONContent;
  const firstParagraph = cloned.content?.find((node) => node.type === 'paragraph');
  if (firstParagraph) {
    const firstText = firstParagraph.content?.find((node) => node.type === 'text');
    if (firstText) {
      firstText.text = `[COPY] ${firstText.text ?? ''}`;
    } else {
      firstParagraph.content = [{ type: 'text', text: '[COPY] ' }, ...(firstParagraph.content ?? [])];
    }
  }
  return cloned;
}

export function dtoToBlockFields(dto: QuestionDefinitionDto): Partial<QuestionBlock> {
  const base = {
    questionDefinitionId: dto.questionDefinitionId,
    description: dto.questionText,
    // NA-382: selection always yields an INHERITING question text, on every
    // type — including a re-selection of the same question, which is easy to
    // assume is a no-op. Clearing the marker discards any override, so wording
    // written against the previous question cannot leak onto the new one.
    descriptionOverridden: undefined,
    // NA-384: same discard-and-repopulate treatment as description, for two
    // different callers: real lesson-block (re)selection (pre-populate the
    // block's title from the bank when it has one) and QuestionCreatePanel's
    // edit/duplicate seed (where `title` means the bank's own Title being
    // edited). Always cleared, regardless of whether the bank has a title.
    titleOverridden: undefined,
    // Selection always discards whatever title was there before, the same
    // way it discards the previous question's description — "discarded and
    // re-populated on reselection" applies even when the newly-selected
    // question has no bank title, so both keys are explicitly cleared here
    // rather than omitted. Omitting them would let a prior selection's
    // title/titleBankValue survive the merge at the call site (real bug:
    // reselecting into a titleless question left the old title showing, with
    // no "Edited" badge, and a stale titleBankValue that would have
    // mis-gated the next manual edit as an override of a bank value that no
    // longer exists). `title: ''` satisfies the required (non-optional)
    // `title` type on Single Choice/Categorization the same way the block
    // factory's own default does.
    ...(dto.title !== null
      ? { title: dto.title, titleBankValue: dto.title }
      : { title: '', titleBankValue: undefined }),
  };

  if (dto.blockType === 'likert-scale') {
    const settings = deriveLikertSettings(dto);
    return { ...base, ...settings } satisfies Partial<LikertScaleBlock>;
  }

  if (dto.blockType === 'single-choice-answer') {
    const target = dto.answerOptions.find((option) => option.isTarget);
    const targetId = target ? String(target.id) : undefined;
    return {
      ...base,
      gated: dto.gated,
      options: dto.answerOptions.map((option) => ({ id: String(option.id), label: option.label })),
      // NA-313: selection always yields an INHERITING block. Clearing the marker
      // discards any override, so an explanation authored against the previous
      // question cannot leak onto the newly selected one. Cleared on the ungated
      // branch too — an ungated block has no explanation to own.
      correctExplanationOverridden: undefined,
      ...(dto.gated
        ? { correctOptionId: targetId, correctExplanation: target?.correctAnswerExplanation ?? undefined }
        : { healthyOptionId: targetId, correctExplanation: undefined }),
    } satisfies Partial<SingleChoiceAnswerBlock>;
  }

  if (dto.blockType === 'workplace-color-spectrum') {
    const target = dto.answerOptions.find((option) => option.isTarget);
    return {
      ...base,
      correctAnswer: target ? spectrumColorFromLabel(target.label) : 'green',
      correctAnswerExplanation: target?.correctAnswerExplanation ?? undefined,
      // NA-313: see the Single Choice branch — selection resets ownership.
      correctAnswerExplanationOverridden: undefined,
    } satisfies Partial<WorkplaceColorSpectrumBlock>;
  }

  if (dto.blockType === 'freeform-text') {
    // `purpose` is carried on the scratch object for the modal-mode editor's
    // benefit only (see FreeformTextQuestionDraftBlock) — never a field of
    // the persisted FreeformTextBlock, so it's not part of this `satisfies`.
    return {
      ...base,
      purpose: dto.purpose === 'Reflection' ? 'Reflection' : 'Sentiment',
    } satisfies Partial<FreeformTextQuestionDraftBlock>;
  }

  // dto.imageLibraryItemId is already the encoded slug the picker itself
  // hands out (author-api re-encodes it on the way out), and dto.imageUrl is
  // the resolved image src — pass both straight through rather than
  // stringifying a raw id with no src to show.
  const categories: CategorizationCategory[] = dto.categories.map((category) => ({
    id: String(category.id),
    label: category.label,
    ...(category.emoji
      ? { icon: { type: 'emoji' as const, value: category.emoji } }
      : category.imageLibraryItemId
        ? { icon: { type: 'image' as const, value: category.imageUrl ?? '', imageLibraryItemId: category.imageLibraryItemId } }
        : {}),
  }));
  const items: CategorizationItem[] = dto.items.map((item) => ({
    id: String(item.id),
    label: item.label,
    correctCategoryId: String(item.correctCategoryId),
    ...(item.emoji
      ? { icon: { type: 'emoji' as const, value: item.emoji } }
      : item.imageLibraryItemId
        ? { icon: { type: 'image' as const, value: item.imageUrl ?? '', imageLibraryItemId: item.imageLibraryItemId } }
        : {}),
  }));
  return {
    ...base,
    categories,
    items,
    // Feedback is authored directly on the block — no copy-down from the bank.
  } satisfies Partial<CategorizationBlock>;
}

export const questionBlockTypeLabel = (blockType: QuestionBlockType): string => ({
  'likert-scale': 'Likert Scale',
  'single-choice-answer': 'Single Choice',
  'workplace-color-spectrum': 'Workplace Color Spectrum',
  'categorization': 'Categorization',
  'freeform-text': 'Freeform Text',
}[blockType]);

// ── Translation-mode transforms ──────────────────────────────────────────────

/**
 * Seeds the translation form from a question's RESOLVED fields (localized where
 * a translation exists, English otherwise) so the author edits over real text.
 * Only the type's translatable label maps are seeded, per the shared registry.
 */
export function initialTranslationFields(question: QuestionDefinitionDto): QuestionTranslationFields {
  const translatable = new Set<QuestionTranslationField>(
    QUESTION_TRANSLATABLE_FIELDS[question.blockType],
  );
  const fields: QuestionTranslationFields = { displayText: question.questionText };
  if (translatable.has('options')) {
    fields.options = Object.fromEntries(question.answerOptions.map((o) => [String(o.id), o.label]));
  }
  if (translatable.has('categories')) {
    fields.categories = Object.fromEntries(question.categories.map((c) => [String(c.id), c.label]));
  }
  if (translatable.has('items')) {
    fields.items = Object.fromEntries(question.items.map((i) => [String(i.id), i.label]));
  }
  const explanation = question.answerOptions.find((o) => o.isTarget)?.correctAnswerExplanation;
  if (explanation && translatable.has('correctAnswerExplanation')) {
    fields.correctAnswerExplanation = explanation;
  }
  return fields;
}

/**
 * Sparse patch of only the fields the author actually edited.
 *
 * The form seeds every field with its resolved value, but sending that whole set
 * would persist English copies of untouched fields as human translations —
 * freezing them instead of letting the resolver keep falling back to the live
 * English source. Label maps are diffed per child id for the same reason.
 */
export function changedTranslationFields(
  initial: QuestionTranslationFields,
  current: QuestionTranslationFields,
): QuestionTranslationFields {
  const patch: QuestionTranslationFields = {};
  if (JSON.stringify(current.displayText) !== JSON.stringify(initial.displayText)) {
    patch.displayText = current.displayText;
  }
  if (JSON.stringify(current.correctAnswerExplanation) !== JSON.stringify(initial.correctAnswerExplanation)) {
    patch.correctAnswerExplanation = current.correctAnswerExplanation;
  }
  for (const field of ['options', 'categories', 'items'] as const) {
    const changed = Object.entries(current[field] ?? {})
      .filter(([id, label]) => label !== initial[field]?.[id]);
    if (changed.length > 0) patch[field] = Object.fromEntries(changed);
  }
  return patch;
}

/**
 * Canonical string of a draft's TRANSLATABLE content only, used to decide
 * whether an English edit should raise the human-translation replacement
 * question.
 *
 * Driven by QUESTION_TRANSLATABLE_FIELDS so it stays comparable with the
 * server's own translatable projection in
 * author-api/src/services/questionContentSeeder.ts. Fields the registry does not
 * list — Likert scale labels, Workplace Color Spectrum colour labels — are
 * deliberately excluded: changing them is not a translatable source change.
 */
export function translatableSource(question: QuestionBlock, isPublic: boolean): string {
  const draft = blockToQuestionDraft(question, isPublic);
  const translatable = new Set<QuestionTranslationField>(
    QUESTION_TRANSLATABLE_FIELDS[draft.blockType],
  );
  // New children have no persisted id yet; comparing id + label still detects a
  // rename, an addition, and a removal.
  const labelled = (rows: ReadonlyArray<{ id?: number; label: string }>) =>
    rows.map((row) => ({ id: row.id, label: row.label }));

  return JSON.stringify({
    questionText: draft.questionText,
    ...(translatable.has('options') && 'options' in draft
      ? { options: labelled(draft.options) }
      : {}),
    ...(translatable.has('categories') && 'categories' in draft
      ? { categories: labelled(draft.categories) }
      : {}),
    ...(translatable.has('items') && 'items' in draft
      ? { items: labelled(draft.items) }
      : {}),
    // The gated flag gates whether the explanation is learner-visible at all.
    ...(translatable.has('correctAnswerExplanation')
      && 'correctAnswerExplanation' in draft
      && (draft.blockType !== 'single-choice-answer' || draft.gated)
      ? { correctAnswerExplanation: draft.correctAnswerExplanation }
      : {}),
  });
}
